<script setup lang="ts">
import { computed, onMounted, onUnmounted, watch } from 'vue'
import { storeToRefs } from 'pinia'
import router from '@/router'
import { useDjangoAuthStore } from '@/stores/authDjango'
import { useProjectStore } from '@/stores/project'
import { useAuthorizationStore } from '@/stores/authorization'
import { useProjectUserStore } from '@/stores/projectUser'
import type { Project } from '@/stores/type/project.type'
import {
  CreateOrUpdateDialog,
  DeleteDialog,
  ProjectIndex,
  WorkOrderField
} from '@haskoning/vue-component-library'

const authorizationStore = useAuthorizationStore()
const projectStore = useProjectStore()
const projectUserStore = useProjectUserStore()
const authStore = useDjangoAuthStore()

const { user } = storeToRefs(authStore)
const {
  items,
  currentItem,
  createUpdateDialog,
  deleteDialog,
  deleteItem,
  fetchLoading,
  writeLoading,
  deleteLoading,
  fieldsFiltered
} = storeToRefs(projectStore)

const navigateToProject = (projectID: number) => {
  router.push({ name: 'project-detail', params: { id: projectID } })
}

const handleAddProject = () => {
  projectStore.setCurrentItem({} as Project)
  projectStore.openCreateUpdateDialog()
}
const handleEditProject = (item: Project) => {
  projectStore.setCurrentItem(item)
  projectStore.openCreateUpdateDialog()
}
const handleDeleteProject = (item: Project) => {
  projectStore.setDeleteItem(item)
  projectStore.openDeleteDialog()
}

const codeField = computed(() => {
  return projectStore.fieldsList.find((field: any) => field.parameter === 'work_order')
})

watch(createUpdateDialog, async () => {
  if (!createUpdateDialog.value) {
    projectStore.setCurrentItem({} as Project)
  }
  await projectStore.servicePermissionItems()
})

watch(deleteDialog, () => {
  if (!deleteDialog.value) {
    projectStore.setDeleteItem({} as Project)
  }
})

onMounted(async () => {
  if (!items || !items.value.length) {
    await projectStore.fetchOptions()
    await projectStore.fetchItems()
  }
  await authorizationStore.fetchItems()
  if (user.value) {
    authorizationStore.setCurrentRole([user.value.role])
  }
  await projectStore.servicePermissionItems()
})

onUnmounted(() => {
  projectStore.resetAll()
  projectUserStore.reset()
  authorizationStore.resetState()
})
</script>

<template>
  <div class="fill-height">
    <ProjectIndex
      title="SPIN"
      :items="items"
      :loading="fetchLoading"
      :get-change-permission="projectStore.getChangePermission"
      :get-delete-permission="projectStore.getDeletePermission"
      @navigate-to-project="navigateToProject"
      @add-item="handleAddProject"
      @edit-item="handleEditProject"
      @delete-item="handleDeleteProject"
    />
    <CreateOrUpdateDialog
      v-if="currentItem"
      name="project"
      v-model="createUpdateDialog"
      v-model:item="currentItem"
      :loading="writeLoading"
      :fields="fieldsFiltered"
      @submit="projectStore.postOrUpdate($event)"
    >
      <WorkOrderField
        v-if="codeField"
        :field="codeField"
        validate-code
        v-model="currentItem"
      ></WorkOrderField>
    </CreateOrUpdateDialog>
    <DeleteDialog
      v-if="deleteItem"
      v-model="deleteDialog"
      :loading="deleteLoading"
      :name="deleteItem?.name"
      v-model:item="deleteItem"
      @set-delete-item="deleteItem = $event"
      @delete="projectStore.delete(deleteItem)"
    ></DeleteDialog>
  </div>
</template>

<style scoped>
:deep(.v-table__wrapper table) {
  border: 1px solid lightgrey;
}
</style>
